import { useMutation } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import Button from '@mui/material/Button';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { FormTextField } from 'components/views/forms/form-text-field';
import { useSnackbar } from 'providers/snackbar';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  CampaignCreateTestLead,
  CampaignCreateTestLeadVariables,
} from 'types/apollo/CampaignCreateTestLead';
import { CampaignCreateTestLeadMutation, CampaignTestLeadsQuery } from './test-leads.graphql';

interface FormData {
  firstName: string;
  phone: string;
  source: string;
}

const phoneRegExp = /^\+\d{11}$/g;

export const CreateTestLeadButtonDialog: FC<{ companyId: string; internalCampaignId: string }> = ({
  companyId,
  internalCampaignId,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      phone: '',
      firstName: '',
      source: '',
    },
  });
  const { openSnackbar } = useSnackbar();

  const handleOpen = () => {
    setDialogOpen(true);
  };

  function handleClose() {
    form.reset();
    setDialogOpen(false);
  }

  const [createTestLead] = useMutation<CampaignCreateTestLead, CampaignCreateTestLeadVariables>(
    CampaignCreateTestLeadMutation,
    { refetchQueries: [CampaignTestLeadsQuery] },
  );

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await createTestLead({
        variables: {
          createTestLeadData: {
            companyId,
            campaignId: internalCampaignId,
            phone: input.phone,
            firstName: input.firstName,
            source: input.source,
          },
        },
      });
      openSnackbar({ message: 'Test lead successfully added', severity: 'success' });
      handleClose();
    } catch (err) {
      // caught in GraphQLWrapper
    }
  });

  return (
    <>
      <Button variant="contained" type="button" onClick={handleOpen}>
        Create test lead
      </Button>
      <Dialog onClose={handleClose} open={dialogOpen}>
        <DialogTitle>Create Test Lead</DialogTitle>
        <DialogContent>
          <form onSubmit={onSubmit}>
            <FormProvider {...form}>
              <FormTextField
                form={form}
                name="phone"
                variant="standard"
                customValidate={phoneRegExp}
                customValidateMessage="Must be properly formatted phone like +12345554321"
                helperText="Format: +15554329876"
              />
              <FormTextField form={form} name="firstName" variant="standard" />
              <FormTextField form={form} name="source" variant="standard" optional />
              <FormSubmitRow onCancel={handleClose} cancelAlwaysEnabled />
            </FormProvider>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
